import { useState, useCallback } from "react";
// For important logs only (dev env); otherwise keep silent
// import logger from "@/lib/logger"; // Uncomment if we add frontend logger wrapper
import { useQuoteLinkContext } from "../contexts/QuoteLinkContext";
import api from "@/shared/api";

/**
 * Custom hook for quote link service operations
 * Handles API calls for quote link actions
 */
export const useQuoteLinkService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { updateQuote, addAction } = useQuoteLinkContext();

  // Base API call function (using shared api)
  const apiCall = useCallback(async (method, url, { data, params } = {}) => {
    try {
      setLoading(true);
      setError(null);

      const options = { skipAuth: true, skipAuthRedirect: true };
      let response;
      if (method === "GET") {
        response = await api.get(url, { ...options, params });
      } else if (method === "POST") {
        response = await api.post(url, data || {}, options);
      } else if (method === "PUT") {
        response = await api.put(url, data || {}, options);
      } else if (method === "PATCH") {
        response = await api.patch(url, data || {}, options);
      } else {
        response = await api.get(url, { ...options, params });
      }

      return response.data || response;
    } catch (err) {
      // Extract error message from response body if available
      const responseData = err?.response?.data || {};
      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        err?.message ||
        "Request failed";

      // Preserve code/status/message for callers
      const mapped = {
        message: errorMessage,
        status: err?.response?.status || err?.status,
        code: responseData?.code || err?.code,
      };
      setError(mapped.message);
      throw mapped;
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate quote link
  const validateQuoteLink = useCallback(
    async (tokenId, phone) => {
      return await apiCall("GET", `/quotes/link/${tokenId}`, {
        params: phone ? { phone } : undefined,
      });
    },
    [apiCall]
  );

  // Normalize phone number to South African format
  const normalizePhone = (phone) => {
    if (!phone) return phone;
    const cleanPhone = phone.replace(/[\s\-()]/g, "");
    if (cleanPhone.startsWith("0")) return "+27" + cleanPhone.substring(1);
    if (!cleanPhone.startsWith("+27"))
      return "+27" + cleanPhone.replace(/^\+?27/, "");
    return cleanPhone;
  };

  // Validate ownership helper (debounced by callers)
  const validateOwnership = useCallback(
    async (tokenId, phone) => {
      try {
        const normalized = normalizePhone(phone);
        if (!normalized) return { verified: false, reason: "EMPTY" };
        const res = await validateQuoteLink(tokenId, normalized);

        // Prefer explicit phoneVerified flag when provided
        if (typeof res?.phoneVerified === "boolean") {
          return res.phoneVerified
            ? { verified: true }
            : { verified: false, code: "PHONE_MISMATCH" };
        }

        // Any explicit success from backend implies verified
        if (res?.success === true) {
          return { verified: true };
        }

        // Inspect 2xx response body for mismatch indicators
        const msg = res?.error || res?.message || "";
        if (
          res?.success === false &&
          (res?.code === "PHONE_MISMATCH" ||
            /mismatch|access denied/i.test(msg))
        ) {
          return { verified: false, code: "PHONE_MISMATCH" };
        }

        // Default: not verified when we can't confirm
        return { verified: false };
      } catch (e) {
        const msg = e?.message || "";
        // Log minimal context for debugging
        // if (logger?.logWarn) logger.logWarn("validateOwnership error", { status: e?.status, message: msg, code: e?.code });

        // Map common backend mismatch patterns
        if (e?.status === 403 || e?.code === "PHONE_MISMATCH") {
          return { verified: false, code: "PHONE_MISMATCH" };
        }
        if (e?.status === 404 && /mismatch|access denied/i.test(msg)) {
          return { verified: false, code: "PHONE_MISMATCH" };
        }
        if (e?.status === 404) return { verified: false, code: "NOT_FOUND" };
        return { verified: false, code: e?.code };
      }
    },
    [validateQuoteLink]
  );

  // Accept quote
  const acceptQuote = useCallback(
    async (tokenId, phone, data = {}) => {
      const normalizedPhone = normalizePhone(phone);
      const result = await apiCall("POST", `/quotes/link/${tokenId}/accept`, {
        data: { phone: normalizedPhone, termsAccepted: true, ...data },
      });

      if (result.success) {
        updateQuote({ status: "accepted" });
        addAction({
          action: "accept",
          timestamp: new Date().toISOString(),
          data: { acceptedAt: new Date().toISOString(), termsAccepted: true },
        });
      }

      return result;
    },
    [apiCall, updateQuote, addAction]
  );

  // Reject quote
  const rejectQuote = useCallback(
    async (tokenId, phone, data = {}) => {
      const normalizedPhone = normalizePhone(phone);
      const result = await apiCall("POST", `/quotes/link/${tokenId}/reject`, {
        data: { phone: normalizedPhone, ...data },
      });

      if (result.success) {
        updateQuote({ status: "rejected" });
        addAction({
          action: "reject",
          timestamp: new Date().toISOString(),
          data: { rejectedAt: new Date().toISOString(), ...data },
        });
      }

      return result;
    },
    [apiCall, updateQuote, addAction]
  );

  // Request call
  const requestCall = useCallback(
    async (tokenId, phone, data = {}) => {
      const normalizedPhone = normalizePhone(phone);
      const result = await apiCall(
        "POST",
        `/quotes/link/${tokenId}/request-call`,
        {
          data: { phone: normalizedPhone, ...data },
        }
      );

      if (result.success) {
        addAction({
          action: "request_call",
          timestamp: new Date().toISOString(),
          data: { requestedAt: new Date().toISOString(), ...data },
        });
      }

      return result;
    },
    [apiCall, addAction]
  );

  // Submit payment proof
  const submitPaymentProof = useCallback(
    async (tokenId, phone, paymentData) => {
      const normalizedPhone = normalizePhone(phone);
      const url = `/quotes/link/${tokenId}/submit-payment?phone=${encodeURIComponent(
        normalizedPhone
      )}`;

      const mapMethod = (m) => {
        if (m === "eft") return "bank_transfer";
        if (m === "cash_deposit") return "cash";
        return m || "bank_transfer";
      };

      try {
        setLoading(true);
        setError(null);
        const formData = new FormData();
        formData.append("amount", paymentData.amount || "");
        formData.append("paymentMethod", mapMethod(paymentData.paymentMethod));
        formData.append("notes", paymentData.notes || "");

        const files = Array.isArray(paymentData.proofFiles)
          ? paymentData.proofFiles
          : [];
        for (const f of files) {
          if (f?.file instanceof File) {
            formData.append(
              "proofFiles",
              f.file,
              f.name || f.file.name || "proof"
            );
          } else if (f instanceof File) {
            formData.append("proofFiles", f, f.name || "proof");
          }
        }

        const response = await api.post(url, formData, {
          skipAuth: true,
          skipAuthRedirect: true,
        });
        const raw = response?.data || response;

        // Normalize success shape: treat any 2xx without explicit error as success
        // Backend may return { ok: true } or omit success entirely
        const inferredSuccess =
          (typeof raw?.success === "boolean" && raw.success === true) ||
          (typeof raw?.ok === "boolean" && raw.ok === true) ||
          (raw && !raw.error && typeof raw?.success === "undefined");

        const result = { ...raw, success: inferredSuccess };

        if (!result.success && result?.error) {
          throw {
            message: result.error,
            status: response?.status,
            code: result?.code,
          };
        }

        if (result.success) {
          updateQuote({ status: "pending_payment" });
          addAction({
            action: "submit_payment",
            timestamp: new Date().toISOString(),
            data: { submittedAt: new Date().toISOString(), ...paymentData },
          });
        }
        return result;
      } catch (e) {
        setError(e?.message || "Upload failed");
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [updateQuote, addAction]
  );

  const getQuoteLinkStatus = useCallback(
    async (tokenId) => {
      return await apiCall("GET", `/quotes/link/${tokenId}/status`);
    },
    [apiCall]
  );

  const getCustomerQuoteLinks = useCallback(
    async (phone) => {
      return await apiCall(
        "GET",
        `/quotes/link/customer/${encodeURIComponent(phone)}`
      );
    },
    [apiCall]
  );

  return {
    loading,
    error,
    validateQuoteLink,
    validateOwnership,
    acceptQuote,
    rejectQuote,
    requestCall,
    submitPaymentProof,
    getQuoteLinkStatus,
    getCustomerQuoteLinks,
  };
};
